import type {Thread} from '@github-ui/diff-lines/conversation-types'

import type {ConversationItem} from './build-conversation-items'
import {CommentsSort} from '../use-comments-filter-state'

function compareThreadsByDiffOrder(a: Thread, b: Thread, diffPathOrderMap: Map<string, number>) {
  if (diffPathOrderMap.size === 0) return 0

  const pathA = a.path ?? a.positioning?.path
  const pathB = b.path ?? b.positioning?.path
  const orderA = pathA !== undefined ? diffPathOrderMap.get(pathA) : undefined
  const orderB = pathB !== undefined ? diffPathOrderMap.get(pathB) : undefined

  // Threads without a matching diff entry sort to the end
  if (orderA === undefined && orderB === undefined) return 0
  if (orderA === undefined) return 1
  if (orderB === undefined) return -1
  if (orderA !== orderB) return orderA - orderB

  // Same file: sort by line number
  const lineA = a.subjectType === 'FILE' ? 0 : (a.positioning?.endLine ?? a.line ?? a.subject?.endLine)
  const lineB = b.subjectType === 'FILE' ? 0 : (b.positioning?.endLine ?? b.line ?? b.subject?.endLine)
  return (lineA ?? Infinity) - (lineB ?? Infinity)
}

/**
 * Sorts thread previews by diff path order and line number. Does not mutate input array.
 * Threads are first ordered by their position in the diff (using diffPathOrderMap),
 * then by line number within the same file.
 * Threads without a matching diff entry are sorted to the end.
 */
export function sortThreadItemsByDiffOrder(
  items: ConversationItem[],
  diffPathOrderMap: Map<string, number>,
): ConversationItem[] {
  return items.toSorted((a, b) => {
    if (a.type !== 'thread' || b.type !== 'thread') return 0
    return compareThreadsByDiffOrder(a.data, b.data, diffPathOrderMap)
  })
}

export function sortConversationItemsByDate(
  items: ConversationItem[],
  sort: typeof CommentsSort.NewestFirst | typeof CommentsSort.OldestFirst,
): ConversationItem[] {
  const oldestFirst = items.toSorted((a, b) => (a.sortDate < b.sortDate ? -1 : a.sortDate > b.sortDate ? 1 : 0))
  return sort === CommentsSort.NewestFirst ? oldestFirst.toReversed() : oldestFirst
}
